import { useCallback, useEffect, useMemo, useRef, useState, memo, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Button, Modal, Form, InputGroup, Dropdown, Pagination } from "react-bootstrap";
import { BsSearch, BsSortDown } from "react-icons/bs";
import { useGetEventUsers } from "../../api/queries/useGetEventUsers";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useGetConnectionRequests } from "../../api/queries/useGetConnectionRequests";
import { useGetSentConnectionRequests } from "../../api/queries/useGetSentConnectionsRequests";
import { useSendConnectionReq } from "../../api/mutations/useSendConnectionReq";
import { useCancelConnectionRequest } from "../../api/mutations/useCancelConnectionRequest";
import { useRespondConnection } from "../../api/mutations/useRespondConnection";
import { useDeleteConnection } from "../../api/mutations/useDeleteConnection";
import { useSendEmail } from "../../api/mutations/useSendEmail";
import { useAppContext } from "../../Context/AppContext";
import { useMembershipSidebarGate } from "../../hooks/useMembershipSidebarGate";
import { User } from "../../types/Types";
import { toast } from "react-toastify";
import EventAttendeeCard, { type AttendeeWithState } from "./EventAttendeeCard";
import { useTranslation } from "react-i18next";
import { useLiveTranslatedInput } from "../../hooks/useLiveTranslatedInput";
import {
  getConnectionRequestEmailBody,
  getAcceptanceEmailBody,
} from "../../utils/connectionEmailUtils";
import { resolvedObjectId } from "../../utils/connectionUserUtils";

const ProfileModal = lazy(() => import("../common/ProfileModal"));

/** Event user from API - may include userId for fetching full profile */
interface EventUserRecord {
  userId?: string;
  firstName: string;
  lastName: string;
  email: string;
  [key: string]: unknown;
}

function pickField(
  r: Record<string, unknown>,
  camel: string,
  pascal: string
): unknown {
  return r[camel] ?? r[pascal];
}

/**
 * API rows may nest the member under `user` / `User`. Nested fields should win
 * for identity (objectId, names) when the root duplicates the viewer on every row.
 */
function flattenEventUserRow(raw: EventUserRecord): Record<string, unknown> {
  const r = raw as Record<string, unknown>;
  const nested =
    pickField(r, "user", "User") ??
    pickField(r, "applicationUser", "ApplicationUser") ??
    pickField(r, "member", "Member");
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...r, ...(nested as Record<string, unknown>) };
  }
  return r;
}

function getEventAttendeeObjectId(raw: EventUserRecord): string {
  const r = flattenEventUserRow(raw);
  const id =
    pickField(r, "objectId", "ObjectId") ??
    pickField(r, "userId", "UserId") ??
    pickField(r, "azureAdObjectId", "AzureAdObjectId");
  return String(id ?? "").trim();
}

function stableNumericId(objectId: string, index: number): number {
  if (!objectId) return -(index + 1);
  let h = 0;
  for (let i = 0; i < objectId.length; i++) {
    h = (Math.imul(31, h) + objectId.charCodeAt(i)) | 0;
  }
  const abs = Math.abs(h);
  return abs > 0 ? abs : index + 1;
}

function normalizedEmail(user: Pick<User, "email"> | null | undefined): string {
  return String(user?.email ?? "").trim().toLowerCase();
}

function normalizedFullName(
  user: Pick<User, "firstName" | "lastName"> | null | undefined
): string {
  return `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim().toLowerCase();
}

/**
 * Event attendee rows can carry inconsistent ids/objectIds, so prefer the same
 * stable identifiers we rely on in the Connections area: email first, then
 * objectId, then numeric id only when there is no contradictory name data.
 */
function sameConnectionUser(attendee: User, candidate: User): boolean {
  const attendeeEmail = normalizedEmail(attendee);
  const candidateEmail = normalizedEmail(candidate);
  if (attendeeEmail && candidateEmail) {
    return attendeeEmail === candidateEmail;
  }

  if (
    typeof attendee.id === "number" &&
    attendee.id > 0 &&
    typeof candidate.id === "number" &&
    candidate.id > 0 &&
    attendee.id === candidate.id
  ) {
    const attendeeName = normalizedFullName(attendee);
    const candidateName = normalizedFullName(candidate);
    return !attendeeName || !candidateName || attendeeName === candidateName;
  }

  const attendeeOid = resolvedObjectId(attendee).toLowerCase();
  const candidateOid = resolvedObjectId(candidate).toLowerCase();
  if (attendeeOid && candidateOid) {
    if (attendeeOid !== candidateOid) return false;
    const attendeeName = normalizedFullName(attendee);
    const candidateName = normalizedFullName(candidate);
    return !attendeeName || !candidateName || attendeeName === candidateName;
  }

  return false;
}

function mergeAttendeeDisplayUser(base: User, preferred?: User | null): User {
  if (!preferred) return base;

  return {
    ...preferred,
    ...base,
    id: preferred.id || base.id,
    objectId: resolvedObjectId(preferred) || base.objectId,
    membershipId: preferred.membershipId || base.membershipId,
    membershipName: preferred.membershipName || base.membershipName,
    city: preferred.city || base.city,
    country: preferred.country || base.country,
    phoneNumber: preferred.phoneNumber || base.phoneNumber,
    professionalPhoto:
      preferred.professionalPhoto ||
      preferred.ProfessionalPhoto ||
      preferred.personalProfessionalPhoto ||
      preferred.PersonalProfessionalPhoto ||
      base.professionalPhoto,
    ProfessionalPhoto:
      preferred.ProfessionalPhoto ||
      preferred.professionalPhoto ||
      preferred.PersonalProfessionalPhoto ||
      preferred.personalProfessionalPhoto ||
      base.ProfessionalPhoto,
    personalProfessionalPhoto:
      preferred.personalProfessionalPhoto ||
      preferred.PersonalProfessionalPhoto ||
      preferred.professionalPhoto ||
      preferred.ProfessionalPhoto ||
      base.personalProfessionalPhoto,
    PersonalProfessionalPhoto:
      preferred.PersonalProfessionalPhoto ||
      preferred.personalProfessionalPhoto ||
      preferred.ProfessionalPhoto ||
      preferred.professionalPhoto ||
      base.PersonalProfessionalPhoto,
  };
}

/**
 * Build a User from /Event/event/:id/users row (same source as admin Event Details).
 * Used when ByObjectId returns the wrong user (e.g. always the signed-in user for non-admins).
 */
function userFromEventAttendeeRecord(raw: EventUserRecord, index: number): User {
  const r = flattenEventUserRow(raw);
  const str = (camel: string, pascal: string) =>
    String(pickField(r, camel, pascal) ?? "").trim();
  const objectId = getEventAttendeeObjectId(raw);
  const idRaw = pickField(r, "id", "Id");
  const parsed = typeof idRaw === "number" ? idRaw : Number(idRaw);
  const id =
    Number.isFinite(parsed) && parsed > 0 ? parsed : stableNumericId(objectId, index);

  const membershipName =
    str("membershipName", "MembershipName") ||
    str("membership", "Membership");

  return {
    id,
    objectId,
    firstName: str("firstName", "FirstName"),
    lastName: str("lastName", "LastName"),
    email: str("email", "Email"),
    isGDPRConsent: 0,
    isEmailUpdates: 0,
    isAnnualMembership: 0,
    membershipId: Number(pickField(r, "membershipId", "MembershipId")) || 0,
    membershipName,
    isDirectory: 0,
    isTermConditions: 0,
    hasPaid: 0,
    dateOfBirth: str("dateOfBirth", "DateOfBirth"),
    idDocument: "",
    phoneNumber: str("phoneNumber", "PhoneNumber"),
    city: str("city", "City"),
    country: str("country", "Country"),
    gender: str("gender", "Gender"),
    languagePreference: str("languagePreference", "LanguagePreference"),
    professionalPhoto: str("professionalPhoto", "ProfessionalPhoto"),
    aboutYou: str("aboutYou", "AboutYou"),
    educationLevel: str("educationLevel", "EducationLevel"),
    studyInterest: str("studyInterest", "StudyInterest"),
    skillsExperience: str("skillsExperience", "SkillsExperience"),
    mentorshipObjectives: str("mentorshipObjectives", "MentorshipObjectives"),
    mentorshipExpertise: str("mentorshipExpertise", "MentorshipExpertise"),
    mentorshipFormat: str("mentorshipFormat", "MentorshipFormat"),
    platformInterest: str("platformInterest", "PlatformInterest"),
    peerNetworking: str("peerNetworking", "PeerNetworking"),
    productInterested: str("productInterested", "ProductInterested"),
    businessName: str("businessName", "BusinessName"),
    businessLogo: str("businessLogo", "BusinessLogo"),
    businessSectorIndustry: str(
      "businessSectorIndustry",
      "BusinessSectorIndustry"
    ),
    businessWebsiteUrl: str("businessWebsiteUrl", "BusinessWebsiteUrl"),
    businessCountryOperation: str(
      "businessCountryOperation",
      "BusinessCountryOperation"
    ),
    businessAbout: str("businessAbout", "BusinessAbout"),
    businessAddressLineOne: str(
      "businessAddressLineOne",
      "BusinessAddressLineOne"
    ),
    businessAddressLineTwo: str(
      "businessAddressLineTwo",
      "BusinessAddressLineTwo"
    ),
    businessCountry: str("businessCountry", "BusinessCountry"),
    businessPostCode: str("businessPostCode", "BusinessPostCode"),
    businessTelephoneNumber: str(
      "businessTelephoneNumber",
      "BusinessTelephoneNumber"
    ),
    businessCurrency: str("businessCurrency", "BusinessCurrency"),
    businessTurnover: str("businessTurnover", "BusinessTurnover"),
    businessObjectives: str("businessObjectives", "BusinessObjectives"),
    businessProductServices: str(
      "businessProductServices",
      "BusinessProductServices"
    ),
    businessDiscount: str("businessDiscount", "BusinessDiscount"),
    businessDiscountCode: str("businessDiscountCode", "BusinessDiscountCode"),
    personalGender: str("personalGender", "PersonalGender"),
    personalLanguagePreference: str(
      "personalLanguagePreference",
      "PersonalLanguagePreference"
    ),
    personalAbout: str("personalAbout", "PersonalAbout"),
    personalProfessionalPhoto: str(
      "personalProfessionalPhoto",
      "PersonalProfessionalPhoto"
    ),
    personalPositionCompany: str(
      "personalPositionCompany",
      "PersonalPositionCompany"
    ),
    hasCompletedProfile: 0,
    county: str("county", "County"),
    referralCode: str("referralCode", "ReferralCode"),
    referralCodeUsed: str("referralCodeUsed", "ReferralCodeUsed"),
  };
}

const BACK_LINK = "/event-booking";
const PAGE_SIZE = 10;

function EventAttendees() {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { objectId, user: currentUser } = useAppContext();
  const { lockSidebarForMembership } = useMembershipSidebarGate();
  const userId = objectId ?? "";

  const [searchTerm, setSearchTerm] = useState("");
  const {
    bindLiveText: bindAttendeeSearch,
    retranslateCurrent: retranslateAttendeeSearch,
    messageTargetLang: attendeeSearchTargetLang,
  } = useLiveTranslatedInput();
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const [sortOption, setSortOption] = useState("connections.sort.recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedForIgnore, setSelectedForIgnore] = useState<number | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [connectionToRemove, setConnectionToRemove] = useState<User | null>(null);
  const [sendConnectionId, setSendConnectionId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<User | null>(null);

  const {
    data: eventUsersData,
    isLoading: loadingAttendees,
  } = useGetEventUsers({ id: eventId || "", enabled: !!eventId });
  const { data: eventData } = useGetEvent({ eventId: eventId || "", enabled: !!eventId });
  const { data: connections, refetch: refetchConnections } = useGetConnections({ userId });
  const { data: connectionRequests, refetch: refetchConnectionRequests } = useGetConnectionRequests({ userId });
  const { data: sentConnectionRequests, refetch: refetchSentConnectionRequests } = useGetSentConnectionRequests({ userId });

  const lastConnectedUserRef = useRef<User | null>(null);

  const { mutate: sendMail } = useSendEmail({ onSuccess: () => setSelectedUser(null) });

  const { mutate: sendConnectionReq } = useSendConnectionReq({
    onSuccess: () => {
      setSendConnectionId(null);
      toast.success(t("connections.requestSentSuccess"));
      const u = lastConnectedUserRef.current;
      if (u?.email) {
        sendMail({
          email: u.email,
          name: `${currentUser?.user?.firstName ?? ""} ${currentUser?.user?.lastName ?? ""}`.trim(),
          subject: `${u.firstName ?? ""} You've received a new connection request`,
          body: getConnectionRequestEmailBody(u.firstName),
        });
      }
      lastConnectedUserRef.current = null;
      refetchSentConnectionRequests();
    },
    onError: () => {
      setSendConnectionId(null);
      lastConnectedUserRef.current = null;
      toast.error(t("connections.requestSendFailed"));
    },
  });

  const { mutate: cancelRequest } = useCancelConnectionRequest({
    onSuccess: () => {
      setCancellingId(null);
      toast.success(t("connections.requestCancelled"));
      refetchSentConnectionRequests();
    },
    onError: () => {
      setCancellingId(null);
      toast.error(t("connections.cancelFailed"));
    },
  });

  const { mutate: respondConnection } = useRespondConnection({
    onSuccess: () => {
      if (selectedRequest?.email) {
        sendMail({
          email: selectedRequest.email,
          name: `${selectedRequest.firstName} ${selectedRequest.lastName}`,
          subject: `${selectedRequest.firstName} Your connection request was accepted`,
          body: getAcceptanceEmailBody(selectedRequest.firstName),
        });
      }
      setSelectedRequest(null);
      setSelectedForIgnore(null);
      refetchConnections();
      refetchConnectionRequests();
    },
    onError: () => {},
  });

  const { mutate: deleteConnection } = useDeleteConnection({
    onSuccess: () => {
      toast.success(t("connections.removedSuccess"));
      setShowRemoveModal(false);
      setConnectionToRemove(null);
      refetchConnections();
    },
    onError: () => toast.error(t("connections.removeError")),
  });

  const connectionUsers = useMemo(
    () => connections?.userDtos ?? [],
    [connections?.userDtos]
  );
  const sentRequestUsers = useMemo(
    () => sentConnectionRequests?.userDtos ?? [],
    [sentConnectionRequests?.userDtos]
  );
  const receivedRequestUsers = useMemo(
    () => connectionRequests?.userDtos ?? [],
    [connectionRequests?.userDtos]
  );

  const attendeesList = useMemo(() => {
    const rows = (eventUsersData?.users as EventUserRecord[] | undefined) ?? [];
    return rows
      .filter((u) => getEventAttendeeObjectId(u))
      .map((eu, idx) => userFromEventAttendeeRecord(eu, idx));
  }, [eventUsersData?.users]);

  const attendeesWithState = useMemo((): AttendeeWithState[] => {
    return attendeesList.map((user) => {
      let connectionState: AttendeeWithState["connectionState"] = "none";
      let actionUser = user;

      const matchedConnection = connectionUsers.find((candidate) =>
        sameConnectionUser(user, candidate)
      );
      const matchedSentRequest = sentRequestUsers.find((candidate) =>
        sameConnectionUser(user, candidate)
      );
      const matchedReceivedRequest = receivedRequestUsers.find((candidate) =>
        sameConnectionUser(user, candidate)
      );

      if (matchedConnection) {
        connectionState = "connected";
        actionUser = matchedConnection;
      } else if (matchedSentRequest) {
        connectionState = "sent";
        actionUser = matchedSentRequest;
      } else if (matchedReceivedRequest) {
        connectionState = "received";
        actionUser = matchedReceivedRequest;
      }

      const matchedCurrentUser =
        currentUser?.user && sameConnectionUser(user, currentUser.user)
          ? currentUser.user
          : null;

      return {
        user: mergeAttendeeDisplayUser(
          user,
          matchedConnection ??
            matchedSentRequest ??
            matchedReceivedRequest ??
            matchedCurrentUser
        ),
        actionUser,
        connectionState,
      };
    });
  }, [
    attendeesList,
    connectionUsers,
    sentRequestUsers,
    receivedRequestUsers,
    currentUser?.user,
  ]);

  /** Canonical viewer object id (context wins, then profile). */
  const viewerOidNorm = useMemo(() => {
    const fromCtx = (objectId ?? "").toString().trim().toLowerCase();
    const fromProf = resolvedObjectId(currentUser?.user).toLowerCase();
    return fromCtx || fromProf;
  }, [objectId, currentUser?.user]);

  /** How many attendee rows share the viewer's object id (API sometimes duplicates it on every row). */
  const viewerOidDuplicateCount = useMemo(() => {
    if (!viewerOidNorm) return 0;
    return attendeesList.filter(
      (u) => resolvedObjectId(u).toLowerCase() === viewerOidNorm
    ).length;
  }, [attendeesList, viewerOidNorm]);

  const filteredAndSorted = useMemo(() => {
    let list = [...attendeesWithState];
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter((a) => {
        const first = (a.user.firstName || "").toLowerCase();
        const last = (a.user.lastName || "").toLowerCase();
        return first.includes(term) || last.includes(term) || `${first} ${last}`.trim().includes(term);
      });
    }
    if (sortOption === "connections.sort.firstName")
      list.sort((a, b) => (a.user.firstName || "").trim().localeCompare((b.user.firstName || "").trim()));
    else if (sortOption === "connections.sort.lastName")
      list.sort((a, b) => (a.user.lastName || "").trim().localeCompare((b.user.lastName || "").trim()));
    return list;
  }, [attendeesWithState, searchTerm, sortOption]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / PAGE_SIZE));
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const showPagination = filteredAndSorted.length > PAGE_SIZE;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOption]);

  useEffect(() => {
    retranslateAttendeeSearch(() => searchTermRef.current, setSearchTerm);
  }, [attendeeSearchTargetLang, retranslateAttendeeSearch]);

  const handleViewProfile = useCallback((user: User) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  }, []);

  const handleMessage = useCallback((id: number) => navigate(`/messages/${id}`), [navigate]);

  const handleConnect = useCallback(
    (user: User) => {
      setSendConnectionId(user.id);
      lastConnectedUserRef.current = user;
      sendConnectionReq({ userId, receiverId: user.id });
    },
    [userId, sendConnectionReq]
  );

  const handleCancelRequest = useCallback(
    (id: number) => {
      setCancellingId(id);
      if (!objectId) return;
      cancelRequest({ id, objectId });
    },
    [objectId, cancelRequest]
  );

  const handleAccept = useCallback(
    (id: number, req: User) => {
      respondConnection({ requestId: id, senderId: currentUser?.user?.id ?? 0, accept: true });
      setSelectedRequest(req);
    },
    [currentUser?.user?.id, respondConnection]
  );

  const handleIgnore = useCallback((id: number) => setSelectedForIgnore(id), []);

  const handleConfirmIgnore = useCallback(() => {
    if (selectedForIgnore !== null) {
      respondConnection({ requestId: selectedForIgnore, senderId: currentUser?.user?.id ?? 0, accept: false });
      setSelectedForIgnore(null);
    }
  }, [selectedForIgnore, currentUser?.user?.id, respondConnection]);

  const handleRemoveConnection = useCallback((user: User) => {
    setConnectionToRemove(user);
    setShowRemoveModal(true);
  }, []);

  const confirmRemove = useCallback(() => {
    if (connectionToRemove && objectId) deleteConnection({ objectId, id: connectionToRemove.id });
  }, [connectionToRemove, objectId, deleteConnection]);

  if (!eventId) {
    return (
      <main className="page-content w-100 p-4 pt-5 m-0">
        <p className="text-danger">{t("events.invalidEvent")}</p>
        <Button variant="link" onClick={() => navigate(BACK_LINK)}>
          {t("events.backToEvents")}
        </Button>
      </main>
    );
  }

  return (
    <main className="page-content w-100 p-4 pt-5 pt-md-5 m-0 d-flex flex-column min-vh-100" aria-label={t("events.attendeesAria")}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .connect-btn { background: linear-gradient(90deg, #2366CD 0%, #3A97A4 100%) !important; }
          .cancel-request-btn { background-color: #FFFFFF !important; border-color: #DC3545 !important; color: #DC3545 !important; }
          .cancel-request-btn:hover { background-color: #DC3545 !important; color: #FFFFFF !important; }
          .avatar-initials { background: linear-gradient(135deg, #2366CD 0%, #3A97A4 100%); }
          .avatar-size { width: 56px; height: 56px; }
          .action-button-group { min-width: 120px; }
          .action-button { min-height: 38px; font-size: 13px; }
          .upgrade-message { border-left: 4px solid #0d6efd; white-space: normal; min-height: 38px; font-size: 10px; padding: 6px 10px; }
          .dropdown-toggle-custom { min-width: 36px; height: 38px; background-color: transparent; }
        `,
        }}
      />

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
        <div>
          <h5 className="fw-bold mb-0 text-dark">
            {t("events.attendeesTitle")} {eventData?.name ? `- ${eventData.name}` : ""}
          </h5>
          <p className="text-muted small mb-0 mt-1">
            {showPagination
              ? t("events.attendeesRange", {
                  start: (currentPage - 1) * PAGE_SIZE + 1,
                  end: Math.min(currentPage * PAGE_SIZE, filteredAndSorted.length),
                  total: filteredAndSorted.length,
                })
              : t("events.attendeesCount", { count: filteredAndSorted.length })}
          </p>
        </div>
      </div>

      <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
        <InputGroup className="rounded-2 flex-grow-1">
          <InputGroup.Text className="bg-white border-end-0 rounded-start">
            <BsSearch size={16} className="text-secondary" />
          </InputGroup.Text>
          <Form.Control
            placeholder={t("events.searchByName")}
            lang={attendeeSearchTargetLang}
            value={searchTerm}
            onChange={(e) =>
              bindAttendeeSearch(e.target.value, setSearchTerm)
            }
            className="border-start-0 rounded-end bg-white"
          />
        </InputGroup>
        <Dropdown onSelect={(k) => k && setSortOption(k)}>
          <Dropdown.Toggle variant="outline-primary" className="rounded-2 shadow-none d-flex align-items-center gap-2">
            <BsSortDown size={16} />
            <span>{t("connections.sort")}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item eventKey="connections.sort.recent">{t("connections.sort.recent")}</Dropdown.Item>
            <Dropdown.Item eventKey="connections.sort.firstName">{t("connections.sort.firstName")}</Dropdown.Item>
            <Dropdown.Item eventKey="connections.sort.lastName">{t("connections.sort.lastName")}</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="flex-grow-1">
        {loadingAttendees ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("feed.loading")}</span>
            </div>
          </div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="bg-light rounded p-5 text-center">
            <div className="mb-3 display-4 opacity-75">👋</div>
            <h6 className="fw-semibold mb-2">{t("events.noAttendeesFound")}</h6>
            <p className="text-muted small mb-0">{t("events.noAttendeesHint")}</p>
          </div>
        ) : (
          <>
            <Row>
              {paginatedList.map((attendee, rowIndex) => {
                const attendeeOid = resolvedObjectId(attendee.user).toLowerCase();
                const viewerNumericId = currentUser?.user?.id ?? 0;
                const viewerEmail = normalizedEmail(currentUser?.user);
                const attendeeEmail = normalizedEmail(attendee.user);
                const idMatchesViewer =
                  viewerNumericId > 0 &&
                  attendee.user.id > 0 &&
                  attendee.user.id === viewerNumericId;
                const emailMatchesViewer =
                  !!viewerEmail &&
                  !!attendeeEmail &&
                  attendeeEmail === viewerEmail;
                const oidMatchesViewer =
                  !!viewerOidNorm &&
                  !!attendeeOid &&
                  attendeeOid === viewerOidNorm;
                const isCurrentUser =
                  idMatchesViewer ||
                  emailMatchesViewer ||
                  (oidMatchesViewer && viewerOidDuplicateCount <= 1);
                return (
                <EventAttendeeCard
                  key={`${resolvedObjectId(attendee.user) || "no-oid"}-${attendee.user.id}-${(currentPage - 1) * PAGE_SIZE + rowIndex}`}
                  attendee={attendee}
                  isCurrentUser={isCurrentUser}
                  isExplorerLogin={lockSidebarForMembership}
                  currentUserMembership={currentUser?.user?.membershipName}
                  onViewProfile={handleViewProfile}
                  onMessage={handleMessage}
                  onConnect={handleConnect}
                  onCancelRequest={handleCancelRequest}
                  onAccept={handleAccept}
                  onIgnore={handleIgnore}
                  onRemoveConnection={handleRemoveConnection}
                  sendConnectionId={sendConnectionId}
                  cancellingId={cancellingId}
                />
                );
              })}
            </Row>

            {showPagination && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  />
                  {[...Array(totalPages)].map((_, i) => (
                    <Pagination.Item
                      key={i + 1}
                      active={i + 1 === currentPage}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      {showProfileModal && (
        <Suspense fallback={null}>
          <ProfileModal
            show={showProfileModal}
            onHide={() => setShowProfileModal(false)}
            selectedUser={selectedUser}
            isConnected={attendeesWithState.some((a) => {
              if (a.connectionState !== "connected" || !selectedUser) return false;
              const ao = resolvedObjectId(a.user).toLowerCase();
              const so = resolvedObjectId(selectedUser).toLowerCase();
              if (ao && so && ao === so) return true;
              return (
                a.user.id > 0 &&
                selectedUser.id > 0 &&
                a.user.id === selectedUser.id
              );
            })}
          />
        </Suspense>
      )}

      <Modal show={!!selectedForIgnore} onHide={() => setSelectedForIgnore(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("connections.confirmIgnore")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("connections.confirmIgnoreText")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedForIgnore(null)}>{t("connections.cancel")}</Button>
          <Button variant="danger" onClick={handleConfirmIgnore}>{t("connections.ignore")}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("connections.confirmRemoval")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("connections.confirmRemovePrompt")}{" "}
          <strong>{connectionToRemove?.firstName} {connectionToRemove?.lastName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>{t("connections.cancel")}</Button>
          <Button variant="danger" onClick={confirmRemove}>{t("connections.remove")}</Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}

export default memo(EventAttendees);
